<template>
  <date-range-picker
    ref="dateRange"
    class="ll-date-range"
    :opens="opens"
    :locale-data="{ firstDay: 1, format: 'mmm d, yyyy', daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] }"
    :min-date="minDate"
    :max-date="maxDate"
    :single-date-picker="singleDatePicker"
    :time-picker="timePicker"
    :time-picker24hour="true"
    :show-week-numbers="false"
    :show-dropdowns="false"
    :auto-apply="singleDatePicker || autoApply"
    :ranges="singleDatePicker ? {} : ranges"
    date-util="moment"
    v-model="dateRange"
    @update="updateValues"
    :linked-calendars="true"
  >
    <template v-slot:input>
      <a-icon type="calendar" /> <span v-html="inputValue" />
    </template>
    <template #ranges="{ ranges: presets }">
      <div class="ranges">
        <ul>
          <li v-for="(range, label) in presets" :key="label" @click="handleRangeSelect(range)">{{ label }}</li>
        </ul>
      </div>
    </template>
  </date-range-picker>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker';
import { getDateRangePresetsInstance } from './DateRangePresets';

/* eslint-disable */
import moment from 'moment-timezone';

const FORMAT = 'YYYY-MM-DD';
const FORMAT_WITH_TIME = 'YYYY-MM-DD HH:mm';

const getDefaultRange = (timePicker = false) => {
  const format = timePicker ? FORMAT_WITH_TIME : FORMAT;
  return {
    startDate: moment(new Date()).format(format),
    endDate: moment(new Date()).endOf('day').format(format),
  };
};

export default {
  name: 'DateRange',
  components: { DateRangePicker },
  props: {
    query: { type: Boolean, default: true },
    opens: { type: String, default: 'left' },
    minDate: { type: [String, Date, null], default: null },
    maxDate: { type: [String, Date, null], default: null },
    autoApply: { type: Boolean, default: false },
    singleDatePicker: { type: Boolean, default: false },
    timePicker: { type: Boolean, default: false },
    autoEmit: { type: Boolean, default: false },
    value: { type: [Object, String, null], default: null },
    disableDates: { type: Function, default: () => ({}) }
  },
  data() {
    return {
      dateRange: { startDate: '', endDate: '' },
      ranges: {},
    };
  },
  computed: {
    currentFormat() {
      return this.timePicker ? FORMAT_WITH_TIME : FORMAT;
    },
    inputValue() {
      if (this.singleDatePicker) {
        const format = this.timePicker ? 'MMM D, YYYY - HH:mm' : 'MMM D, YYYY';
        return `${moment(this.dateRange.startDate).format(format)}`;
      }
      if (this.dateRange.startDate && this.dateRange.endDate) {
        let dateText = '';

        if (!this.timePicker) {
          for (let key in this.ranges) {
            const rangeStart = moment(this.ranges[key][0]).format(FORMAT);
            const rangeEnd = moment(this.ranges[key][1]).format(FORMAT);
            const currentStart = moment(this.dateRange.startDate).format(FORMAT);
            const currentEnd = moment(this.dateRange.endDate).format(FORMAT);
            
            if (rangeStart === currentStart && rangeEnd === currentEnd) {
              if (key === 'Today') {
                dateText = `Today <small>( ${moment().startOf('day').format('MMM, D YYYY')} )</small>`
              } else if (key === 'Yesterday') {
                dateText = `Yesterday <small>( ${moment().subtract(1, 'days').startOf('day').format('MMM, D YYYY')} )</small>`
              } else {
                dateText = key;
              }
              break;
            }
          }
        }
        
        if (dateText.length === 0) {
          if (this.timePicker) {
            return `${moment(this.dateRange.startDate).format('MMM, D YYYY - HH:mm')} - ${moment(this.dateRange.endDate).format('MMM, D YYYY - HH:mm')}`;
          } else {
            return `${moment(this.dateRange.startDate).format('MMM, D YYYY')} - ${moment(this.dateRange.endDate).format('MMM, D YYYY')}`;
          }
        }
        
        return dateText;
      }
      return '';
    }
  },
  created() {
    const dateRangePresets = getDateRangePresetsInstance();
    this.ranges = dateRangePresets.getDateRangesToDate();

    /* ************* Set DateRange ************** */
    let dateRange = null;
    try {
      // Set DateRange from v-model
      dateRange = { startDate: this.value.startDate, endDate: this.value.endDate };
    } catch (err) {
      // Set DateRange from $route URI
      if (this.query) {
        const { startDate, endDate } = this.$route.query;
        if (startDate && endDate) dateRange = { startDate, endDate };
      }
    }
    // If neither set Default DateRange
    if (!dateRange) dateRange = getDefaultRange(this.timePicker);
    this.dateRange = { ...dateRange };
    /* ************* ************* ************** */

    // Sync $route URI
    this.updateQuery(this.dateRange)

    // Auto emit
    if (this.autoEmit) this.emit()
  },
  methods: {
    updateValues({ startDate, endDate }) {
      const formattedStartDate = moment(startDate).format(this.currentFormat);
      const formattedEndDate = moment(endDate).format(this.currentFormat);
      
      this.dateRange = { 
        startDate: formattedStartDate, 
        endDate: formattedEndDate 
      };
      
      this.updateQuery(this.dateRange);
      this.emit();
      
      // Emit both formatted and original moment objects
      this.$emit('change', this.dateRange, { 
        startDate: moment(startDate), 
        endDate: moment(endDate) 
      });
    },
    emit() {
      this.$emit('input', this.dateRange);
      this.$emit('update:label', this.inputValue);
    },
    updateQuery({ startDate, endDate }) {
      if (this.query) {
        const { startDate: qStartDate, endDate: qEndDate } = this.$route.query;
        const { params } = this.$route;
        
        // For URL query, always use the appropriate format
        const queryStartDate = this.timePicker ? startDate : moment(startDate).format(FORMAT);
        const queryEndDate = this.timePicker ? endDate : moment(endDate).format(FORMAT);
        
        if (qStartDate !== queryStartDate || qEndDate !== queryEndDate) {
          this.$router.replace({ 
            params, 
            query: { 
              ...this.$route.query, 
              startDate: queryStartDate, 
              endDate: queryEndDate 
            } 
          });
        }
      }
    },
    handleRangeSelect([startDate, endDate]) {
      this.$refs.dateRange.togglePicker();
      if (this.timePicker) {
        const start = moment(startDate).startOf('day');
        const end = moment(endDate).endOf('day');
        this.updateValues({ startDate: start, endDate: end });
      } else {
        this.updateValues({ startDate, endDate });
      }
    },
  },
};
</script>